import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Paper,
  Box,
  Typography,
  Divider,
  Alert,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import type {
  ReservationSlotTemplate,
  ReservationSlotTemplateInput,
  TreatmentCategory,
  IntervalMinutes,
  PatternType,
  IntervalPattern,
  HourInterval,
} from '../../../../domain/reservationSlots';
import {
  TREATMENT_CATEGORIES,
  TREATMENT_CATEGORY_LABELS,
  expandTemplateToHour,
} from '../../../../domain/reservationSlots';
import CategoryChip from './CategoryChip';

export interface TemplateEditDialogProps {
  open: boolean;
  /** 編集対象のテンプレート（新規作成時はnull） */
  initial: ReservationSlotTemplate | null;
  saving: boolean;
  onClose: () => void;
  onSave: (input: ReservationSlotTemplateInput) => void;
}

const MIN_SLOTS = 1;
const MAX_SLOTS = 8;
const DEFAULT_CATEGORY: TreatmentCategory = 'pill';
/** 新規作成時の1区間あたりの枠数 */
const DEFAULT_SLOTS_PER_INTERVAL = 2;
/** プレビューの基準時刻（「17:00–17:15」のようなイメージ表示に使用する固定値） */
const PREVIEW_BASE_HOUR = 17;
/** プレビュー用に組み立てる仮テンプレートのID（保存には使用しない） */
const PREVIEW_TEMPLATE_ID = 'preview';
/** テンプレート名入力欄のid（外部ラベルとの関連付けに使用する） */
const TEMPLATE_NAME_INPUT_ID = 'template-name-input';

/**
 * 入力欄の上に置く項目ラベルの装飾
 *
 * @description
 * 入力欄の外側にラベルを出し、項目名を太字で見せる。ダイアログ内の全項目で共通。
 */
const FIELD_LABEL_SX: SxProps<Theme> = {
  display: 'block',
  mb: 0.75,
  fontWeight: 700,
  fontSize: 14,
  color: 'text.primary',
};

/**
 * 交互パターン A/B バッジの配色
 *
 * @description
 * 診療カテゴリの配色（CATEGORY_COLORS）とは意味が異なるため、パターン専用の色を定義する。
 */
const PATTERN_BADGE_COLORS: Record<IntervalPattern, { bg: string; fg: string }> = {
  A: { bg: '#EEF2FF', fg: '#4338CA' },
  B: { bg: '#ECFDF5', fg: '#065F46' },
};

/**
 * カテゴリ配列を指定の長さにリサイズする（不足分はデフォルトカテゴリで補完）
 *
 * @param {TreatmentCategory[]} arr - 元のカテゴリ配列
 * @param {number} length - リサイズ後の要素数
 * @returns {TreatmentCategory[]} 指定長のカテゴリ配列
 */
function resizeCategories(arr: TreatmentCategory[], length: number): TreatmentCategory[] {
  const next = arr.slice(0, length);
  while (next.length < length) {
    next.push(DEFAULT_CATEGORY);
  }
  return next;
}

/**
 * 時の開始からの経過分をプレビュー用の時刻文字列へ整形する
 *
 * @description
 * 基準時刻は17時固定。経過分60は翌時（18:00）として扱う。
 *
 * @param {number} minuteOffset - 時の開始からの経過分
 * @returns {string} 「17:15」形式の時刻文字列
 */
function formatPreviewTime(minuteOffset: number): string {
  const hour = PREVIEW_BASE_HOUR + Math.floor(minuteOffset / 60);
  const minute = minuteOffset % 60;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/**
 * 入力欄の上に置く項目ラベル
 *
 * @description
 * `htmlFor` を渡した場合は `label` 要素として入力欄と関連付ける。
 * 単一の入力欄に紐づかない項目（トグル・数値ステッパー）では `div` として表示する。
 */
const FieldLabel: React.FC<{ htmlFor?: string; children: React.ReactNode }> = ({
  htmlFor,
  children,
}) =>
  htmlFor ? (
    <FormLabel htmlFor={htmlFor} sx={FIELD_LABEL_SX}>
      {children}
    </FormLabel>
  ) : (
    <FormLabel component="div" sx={FIELD_LABEL_SX}>
      {children}
    </FormLabel>
  );

/**
 * 診療カテゴリ選択セレクト
 */
const CategorySelect: React.FC<{
  value: TreatmentCategory;
  label: string;
  onChange: (value: TreatmentCategory) => void;
}> = ({ value, label, onChange }) => (
  <FormControl size="small" fullWidth>
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value as TreatmentCategory)}
      aria-label={label}
    >
      {TREATMENT_CATEGORIES.map((category) => (
        <MenuItem key={category} value={category}>
          {TREATMENT_CATEGORY_LABELS[category]}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

/**
 * 「枠 N」ラベル付きのカテゴリ選択行
 */
const SlotCategoryLine: React.FC<{
  index: number;
  value: TreatmentCategory;
  label: string;
  onChange: (value: TreatmentCategory) => void;
}> = ({ index, value, label, onChange }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      py: 0.75,
      borderBottom: '1px solid',
      borderColor: 'divider',
      '&:last-of-type': { borderBottom: 'none' },
    }}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ minWidth: 36, flexShrink: 0, fontWeight: 600 }}
    >
      枠 {index + 1}
    </Typography>
    <CategorySelect value={value} label={label} onChange={onChange} />
  </Box>
);

/**
 * 1時間分のスロットイメージを表示するプレビュー
 */
const HourPreview: React.FC<{ intervals: HourInterval[] }> = ({ intervals }) => (
  <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, backgroundColor: 'grey.100' }}>
    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', mb: 1 }}>
      ▸ プレビュー（1時間分のスロットイメージ）
    </Typography>
    <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
      {intervals.map((interval) => (
        <Paper
          key={interval.index}
          variant="outlined"
          sx={{ flex: '0 0 130px', px: 1, py: 1.25, borderRadius: 1.5 }}
        >
          {interval.pattern !== null && (
            <Box
              sx={{
                mb: 0.5,
                px: 0.75,
                py: 0.25,
                borderRadius: 0.5,
                fontSize: 10,
                fontWeight: 800,
                textAlign: 'center',
                backgroundColor: PATTERN_BADGE_COLORS[interval.pattern].bg,
                color: PATTERN_BADGE_COLORS[interval.pattern].fg,
              }}
            >
              {interval.pattern}
            </Box>
          )}
          <Typography
            sx={{
              fontSize: 10,
              fontFamily: 'monospace',
              textAlign: 'center',
              color: 'text.secondary',
              mb: 0.75,
            }}
          >
            {formatPreviewTime(interval.startMinute)}–{formatPreviewTime(interval.endMinute)}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {interval.slots.map((category, slotIndex) => (
              <CategoryChip key={`${interval.index}-${slotIndex}`} category={category} />
            ))}
          </Box>
        </Paper>
      ))}
    </Box>
  </Box>
);

/**
 * 解放枠テンプレート編集ダイアログ
 *
 * @description
 * テンプレートの新規作成・編集フォーム。枠間隔・パターン・1区間枠数を選択し、
 * 各枠の診療カテゴリを指定する。交互パターンの場合はA/B列を左右に並べて設定する。
 * 入力内容は1時間分のスロットイメージとしてリアルタイムにプレビュー表示される。
 */
const TemplateEditDialog: React.FC<TemplateEditDialogProps> = ({
  open,
  initial,
  saving,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState('');
  const [intervalMinutes, setIntervalMinutes] = useState<IntervalMinutes>(15);
  const [patternType, setPatternType] = useState<PatternType>('single');
  const [slotsPerInterval, setSlotsPerInterval] = useState<number>(DEFAULT_SLOTS_PER_INTERVAL);
  const [slotsA, setSlotsA] = useState<TreatmentCategory[]>(
    resizeCategories([], DEFAULT_SLOTS_PER_INTERVAL)
  );
  const [slotsB, setSlotsB] = useState<TreatmentCategory[]>(
    resizeCategories([], DEFAULT_SLOTS_PER_INTERVAL)
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  // ダイアログを開いた際にフォームを初期化
  useEffect(() => {
    if (!open) {
      return;
    }
    if (initial) {
      setName(initial.name);
      setIntervalMinutes(initial.intervalMinutes);
      setPatternType(initial.patternType);
      setSlotsPerInterval(initial.slotsPerInterval);
      setSlotsA(resizeCategories(initial.slotsA, initial.slotsPerInterval));
      setSlotsB(
        resizeCategories(initial.slotsB ?? [], initial.slotsPerInterval)
      );
    } else {
      setName('');
      setIntervalMinutes(15);
      setPatternType('single');
      setSlotsPerInterval(DEFAULT_SLOTS_PER_INTERVAL);
      setSlotsA(resizeCategories([], DEFAULT_SLOTS_PER_INTERVAL));
      setSlotsB(resizeCategories([], DEFAULT_SLOTS_PER_INTERVAL));
    }
    setValidationError(null);
  }, [open, initial]);

  const handleSlotsPerIntervalChange = useCallback((rawValue: number) => {
    const clamped = Math.max(MIN_SLOTS, Math.min(MAX_SLOTS, Math.floor(rawValue)));
    setSlotsPerInterval(clamped);
    setSlotsA((prev) => resizeCategories(prev, clamped));
    setSlotsB((prev) => resizeCategories(prev, clamped));
  }, []);

  const handlePatternTypeChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, value: PatternType | null) => {
      // 選択済みボタンの再クリック（value=null）では選択を解除しない
      if (value === null) {
        return;
      }
      setPatternType(value);
    },
    []
  );

  const handleSlotAChange = useCallback((index: number, value: TreatmentCategory) => {
    setSlotsA((prev) => prev.map((c, i) => (i === index ? value : c)));
  }, []);

  const handleSlotBChange = useCallback((index: number, value: TreatmentCategory) => {
    setSlotsB((prev) => prev.map((c, i) => (i === index ? value : c)));
  }, []);

  const handleSave = useCallback(() => {
    if (!name.trim()) {
      setValidationError('テンプレート名を入力してください');
      return;
    }
    const input: ReservationSlotTemplateInput = {
      name: name.trim(),
      intervalMinutes,
      patternType,
      slotsPerInterval,
      slotsA: resizeCategories(slotsA, slotsPerInterval),
      slotsB: patternType === 'alternate' ? resizeCategories(slotsB, slotsPerInterval) : null,
    };
    onSave(input);
  }, [name, intervalMinutes, patternType, slotsPerInterval, slotsA, slotsB, onSave]);

  // フォームの現在値から1時間分の区間を算出する（プレビュー用）
  const previewIntervals = useMemo<HourInterval[]>(() => {
    const previewTemplate: ReservationSlotTemplate = {
      id: initial?.id ?? PREVIEW_TEMPLATE_ID,
      name,
      intervalMinutes,
      patternType,
      slotsPerInterval,
      slotsA: resizeCategories(slotsA, slotsPerInterval),
      slotsB: patternType === 'alternate' ? resizeCategories(slotsB, slotsPerInterval) : null,
    };
    return expandTemplateToHour(previewTemplate);
  }, [initial, name, intervalMinutes, patternType, slotsPerInterval, slotsA, slotsB]);

  const isAlternate = patternType === 'alternate';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box>
          <Typography component="div" variant="h6" fontWeight={700}>
            {initial ? 'テンプレートを編集' : 'テンプレートを新規作成'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            1つの時間帯で生成するスロットのパターンを設定します
          </Typography>
        </Box>
        <IconButton size="small" aria-label="閉じる" onClick={onClose} disabled={saving}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {validationError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setValidationError(null)}>
            {validationError}
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <FieldLabel htmlFor={TEMPLATE_NAME_INPUT_ID}>テンプレート名</FieldLabel>
            <TextField
              id={TEMPLATE_NAME_INPUT_ID}
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
              required
            />
          </Box>

          <Box>
            <FieldLabel>枠間隔</FieldLabel>
            <FormControl size="small" fullWidth>
              <Select
                value={intervalMinutes}
                onChange={(e) => setIntervalMinutes(Number(e.target.value) as IntervalMinutes)}
                inputProps={{ 'aria-label': '枠間隔' }}
              >
                <MenuItem value={15}>15分</MenuItem>
                <MenuItem value={30}>30分</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              診療1コマの長さ（15分なら1時間に4区間）
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <FieldLabel>パターンタイプ</FieldLabel>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              color="primary"
              value={patternType}
              onChange={handlePatternTypeChange}
              aria-label="パターンタイプ"
            >
              <ToggleButton
                value="single"
                sx={{ flexDirection: 'column', gap: 0.25, textTransform: 'none', py: 1 }}
              >
                <Typography component="span" variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  単一
                </Typography>
                <Typography component="span" variant="caption" sx={{ lineHeight: 1.2 }}>
                  全区間が同じ構成
                </Typography>
              </ToggleButton>
              <ToggleButton
                value="alternate"
                sx={{ flexDirection: 'column', gap: 0.25, textTransform: 'none', py: 1 }}
              >
                <Typography component="span" variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  交互（A・B）
                </Typography>
                <Typography component="span" variant="caption" sx={{ lineHeight: 1.2 }}>
                  2パターンを交替
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box>
            <FieldLabel>1区間あたりの枠数</FieldLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                aria-label="枠数を減らす"
                onClick={() => handleSlotsPerIntervalChange(slotsPerInterval - 1)}
                disabled={slotsPerInterval <= MIN_SLOTS}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
              <TextField
                type="number"
                value={slotsPerInterval}
                onChange={(e) => handleSlotsPerIntervalChange(Number(e.target.value))}
                size="small"
                inputProps={{
                  min: MIN_SLOTS,
                  max: MAX_SLOTS,
                  'aria-label': '1区間あたりの枠数',
                  style: { textAlign: 'center', fontWeight: 700 },
                }}
                sx={{ width: 76 }}
              />
              <IconButton
                size="small"
                aria-label="枠数を増やす"
                onClick={() => handleSlotsPerIntervalChange(slotsPerInterval + 1)}
                disabled={slotsPerInterval >= MAX_SLOTS}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
              <Typography variant="body2" color="text.secondary">
                枠 / 区間
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              同時に受け付けられる予約数です（{MIN_SLOTS}〜{MAX_SLOTS}）
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {isAlternate ? (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr auto 1fr' },
                gap: 1.5,
                alignItems: 'start',
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  パターン A
                  <Typography component="span" variant="caption" color="text.secondary">
                    （:00/:30 始まりの区間）
                  </Typography>
                </Typography>
                {slotsA.map((category, index) => (
                  <SlotCategoryLine
                    key={`slotA-${index}`}
                    index={index}
                    value={category}
                    label={`A枠${index + 1}`}
                    onChange={(value) => handleSlotAChange(index, value)}
                  />
                ))}
              </Box>
              <Box
                aria-hidden
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.disabled',
                  fontSize: 20,
                  pt: { xs: 0, sm: 4 },
                  transform: { xs: 'rotate(90deg)', sm: 'none' },
                }}
              >
                ⇄
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  パターン B
                  <Typography component="span" variant="caption" color="text.secondary">
                    （:15/:45 始まりの区間）
                  </Typography>
                </Typography>
                {slotsB.map((category, index) => (
                  <SlotCategoryLine
                    key={`slotB-${index}`}
                    index={index}
                    value={category}
                    label={`B枠${index + 1}`}
                    onChange={(value) => handleSlotBChange(index, value)}
                  />
                ))}
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              15分間隔: :00/:30区間はA、:15/:45区間はB / 30分間隔: :00区間はA、:30区間はB
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              枠ごとのカテゴリ
              <Typography component="span" variant="caption" color="text.secondary">
                　全時間帯に同じ構成が繰り返されます
              </Typography>
            </Typography>
            {slotsA.map((category, index) => (
              <SlotCategoryLine
                key={`slotA-${index}`}
                index={index}
                value={category}
                label={`枠${index + 1}`}
                onChange={(value) => handleSlotAChange(index, value)}
              />
            ))}
          </>
        )}

        <HourPreview intervals={previewIntervals} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          キャンセル
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          保存
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateEditDialog;
